import { z } from 'zod';

import { normalizeDecimalNumberInput, normalizeIntegerNumberInput } from '#utils/numberInputParsing.ts';

import { composeDateTimeValue, isValidDateValue, isValidTimeValue } from './timeWindowFields.ts';

export const error = {
    SMALL_STRING: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.small-string',
    LONG_STRING: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.long-string',
    SMALL_NUMBER: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.small-number',
    LONG_NUMBER: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.long-number',
    INVALID_DATE: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.invalid-date',
    WINDOW_DATE_ORDER: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.window-date-order',
    DATE_ORDER: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.date-order',
};

const OrderStopTypeSchema = z.enum(['loadingStop', 'unloadingStop']);
export type OrderStopType = z.infer<typeof OrderStopTypeSchema>;
const OrderMaterialSchema = z.enum(['orderMaterial']);
export type OrderMaterial = z.infer<typeof OrderMaterialSchema>;
export type OrderFormPage = OrderStopType | OrderMaterial;

const LocationSchema = z.object({
    city: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    street: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    country: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    zipCode: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
});

export type Location = z.infer<typeof LocationSchema>;

const StopTypeEnum = z.enum(['load', 'unload', 'unknown']);
const BulkMaterialUnitTypeSchema = z.enum(['kilogram', 'ton', 'liter', 'cubic-meter']);

const normalizeIntegerInput = (value: unknown) => {
    if (typeof value !== 'string') {
        return value;
    }
    const normalizedValue = normalizeIntegerNumberInput(value);
    return normalizedValue !== undefined ? Number(normalizedValue) : Number.NaN;
};

const normalizeDecimalInput = (value: unknown) => {
    if (typeof value !== 'string') {
        return value;
    }
    const normalizedValue = normalizeDecimalNumberInput(value);
    return normalizedValue !== undefined ? Number(normalizedValue) : Number.NaN;
};

const positiveIntegerNumberSchema = z.preprocess(
    normalizeIntegerInput,
    z.number({ invalid_type_error: error.SMALL_NUMBER }).int(error.SMALL_NUMBER).min(1, error.SMALL_NUMBER)
) as z.ZodType<number>;
const decimalNumberSchema = z.preprocess(
    normalizeDecimalInput,
    z.number({ invalid_type_error: error.SMALL_NUMBER })
) as z.ZodType<number>;
const positiveDecimalNumberSchema = z.preprocess(
    normalizeDecimalInput,
    z.number({ invalid_type_error: error.SMALL_NUMBER }).min(1, error.SMALL_NUMBER)
) as z.ZodType<number>;

const StopFormDataSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    startDate: z.string().min(1, error.SMALL_STRING).refine(isValidDateValue, { message: error.INVALID_DATE }),
    startTime: z.string().refine(isValidTimeValue, { message: error.INVALID_DATE }).optional(),
    endDate: z.string().min(1, error.SMALL_STRING).refine(isValidDateValue, { message: error.INVALID_DATE }),
    endTime: z.string().refine(isValidTimeValue, { message: error.INVALID_DATE }).optional(),
    location: LocationSchema,
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    type: StopTypeEnum.optional(),
});

export type StopFormData = z.infer<typeof StopFormDataSchema>;

const tourStopTimeValidation = (schema: StopFormData) => {
    const startDateTime = composeDateTimeValue(schema.startDate, schema.startTime);
    const endDateTime = composeDateTimeValue(schema.endDate, schema.endTime);

    if (startDateTime && endDateTime) {
        const startDate = new Date(startDateTime);
        const endDate = new Date(endDateTime);
        return startDate <= endDate;
    }
    return true;
};

export const StopFormDataValidationSchema = StopFormDataSchema.refine(tourStopTimeValidation, {
    message: error.WINDOW_DATE_ORDER,
    path: ['endDate'],
});

export const CargoMaterialFormDataSchema = z.object({
    type: z.literal('cargo-material'),
    id: z.string().optional(),
    quantity: positiveIntegerNumberSchema,
    name: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    packageType: z.string().min(1, error.SMALL_STRING),
    index: z.number().optional(),

    width: decimalNumberSchema.optional(),
    height: decimalNumberSchema.optional(),
    length: decimalNumberSchema.optional(),
    weight: decimalNumberSchema.optional(),
    loadingMeter: decimalNumberSchema.optional(),
});

export type CargoMaterialFormData = z.infer<typeof CargoMaterialFormDataSchema>;

export const BulkMaterialFormDataSchema = z.object({
    type: z.literal('bulk-material'),
    id: z.string().optional(),
    quantity: positiveDecimalNumberSchema,
    packageType: z.string().min(1, error.SMALL_STRING),
    name: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    unitType: BulkMaterialUnitTypeSchema,
    index: z.number().optional(),
});

export type BulkMaterialFormData = z.infer<typeof BulkMaterialFormDataSchema>;

export const MaterialFormDataSchema = z.union([CargoMaterialFormDataSchema, BulkMaterialFormDataSchema]);
export type MaterialFormData = z.infer<typeof MaterialFormDataSchema>;

export const TransportFormDataSchema = z.object({
    page: OrderStopTypeSchema,
    id: z.string().optional(),
    number: z.string().min(1, error.SMALL_STRING).max(255, error.LONG_STRING),
    customerName: z.string().max(255, error.LONG_STRING),
    loadingComment: z.string().max(1000, error.LONG_STRING).optional(),
    unloadingComment: z.string().max(1000, error.LONG_STRING).optional(),
    loadingStop: StopFormDataValidationSchema,
    unloadingStop: StopFormDataValidationSchema,
    materials: z.any().optional(),
});

export type TransportFormData = z.infer<typeof TransportFormDataSchema>;

export const OrderFormDataSchema = TransportFormDataSchema.extend({
    page: OrderMaterialSchema,
    materials: z.array(z.union([CargoMaterialFormDataSchema, BulkMaterialFormDataSchema])),
});

export const OrderFormWizard = z
    .discriminatedUnion('page', [TransportFormDataSchema, OrderFormDataSchema])
    .refine(
        schema => {
            const pickupDateTime = composeDateTimeValue(schema.loadingStop.endDate, schema.loadingStop.endTime);
            const deliveryDateTime = composeDateTimeValue(schema.unloadingStop.endDate, schema.unloadingStop.endTime);

            if (pickupDateTime && deliveryDateTime) {
                const pickupDate = new Date(pickupDateTime);
                const deliveryDate = new Date(deliveryDateTime);
                return deliveryDate >= pickupDate;
            }
            return true;
        },
        {
            message: error.DATE_ORDER,
            path: ['unloadingStop', 'endDate'],
        }
    )
    .refine(
        data => {
            const pickupDateTime = composeDateTimeValue(data.loadingStop.startDate, data.loadingStop.startTime);
            const deliveryDateTime = composeDateTimeValue(data.unloadingStop.startDate, data.unloadingStop.startTime);

            if (pickupDateTime && deliveryDateTime) {
                const pickupDate = new Date(pickupDateTime);
                const deliveryDate = new Date(deliveryDateTime);
                return deliveryDate >= pickupDate;
            }
            return true;
        },
        {
            message: error.DATE_ORDER,
            path: ['unloadingStop', 'startDate'],
        }
    );

export type OrderFormData = z.infer<typeof OrderFormWizard>;

const UpdateStopFormDataSchema = StopFormDataSchema.extend({ id: z.string() }).refine(tourStopTimeValidation, {
    message: error.WINDOW_DATE_ORDER,
    path: ['endDate'],
});

export const UpdateOrderFormDataSchema = OrderFormDataSchema.extend({
    page: OrderStopTypeSchema,
    id: z.string(),
    loadingStop: UpdateStopFormDataSchema,
    unloadingStop: UpdateStopFormDataSchema,
});

export type UpdateOrderFormData = z.infer<typeof UpdateOrderFormDataSchema>;
